import { Theme } from '@mui/system';
import _ from 'lodash';
import { DateString } from 'routes/HumanResourcesModule/business/types';
import {
    DefaultLabelFormatterCallbackParams,
    EChartsOption,
    SeriesOption,
} from 'echarts';
import {
    DEFAULT_OPTIONS,
    currencyFormatter,
} from 'routes/HumanResourcesModule/components/HrChart/eChartHelpers';
import { getDateColumnLabel } from 'routes/HumanResourcesModule/helpers/dataHelpers';
import {
    ORG_CHART_HR_MAPPING,
    ORG_CHART_HR_NAME,
} from 'routes/HumanResourcesModule/business/definitions';

/**
 * Retrieves the appropriate group field value from a given record.
 * If the group field is ORG_CHART_HR_NAME, it uses ORG_CHART_HR_MAPPING if present, otherwise it uses ORG_CHART_HR_NAME.
 * @param item - The record from which to retrieve the group field value.
 * @param groupField - The key of the group field.
 * @returns The value of the group field or its mapping.
 */
function getGroupFieldValue<T extends Record<string, any>>(
    item: T,
    groupField: keyof T,
): any {
    return groupField === ORG_CHART_HR_NAME
        ? item[ORG_CHART_HR_MAPPING] || item[groupField]
        : item[groupField];
}

/**
 * Format the given pay state data to aliment a Bar chart grouped
 * by date and groupField. Each bar is constituted of several groups (determined by groupField)
 * and there are one Bar by given filtered date.
 *
 * @param payStateData: data to format
 * @param dates: the list of dates to get the data values from.
 * @param groupField: the field that will constitute the groups visible in each bar
 * @param valueField: the prefix of the dated field to get the values from
 * @returns a list of records with as keys the group field and the filtered dates
 *                  as values: the group name, and the values associated to the valueField
 *                  at each of the filteredDates
 */
export function formatDataForDatedBarGraph<
    T extends Record<string, number | string>,
>(
    payStateData: T[],
    dates: DateString[],
    groupField: keyof T,
    valueField: string,
): Record<string | DateString, number | string>[] {
    // we remove the fields other than the groupField and the valueField
    // we remove the prefix (valueField) from the dated fields to keep only
    // the dates
    const dataWithWantedFields = payStateData.map<T>((item) => {
        return {
            [groupField]: getGroupFieldValue(item, groupField),
            ...Object.fromEntries(
                dates.map((date) => [date, item[`${valueField}..${date}`]]),
            ),
        } as T;
    });

    // we use the groupField to obtain a record of each group with all date-values associated
    const dataGrouped = _.groupBy(dataWithWantedFields, groupField);
    // then we sum the values by date, and format the results to get a list of records containing the group name
    // and the summed value for each date in the dates
    const dataGroupedWithSums = Object.keys(dataGrouped).map((groupValue) => {
        return {
            [groupField]: groupValue,
            ...Object.fromEntries(
                dates.map((date) => [
                    date,
                    _.sumBy(dataGrouped[groupValue], date),
                ]),
            ),
        } as T;
    });
    // we filter services with all amounts + 0
    const dataGroupedWithSumsFiltered = dataGroupedWithSums.filter((datum) =>
        dates.some((date) => datum[date] !== 0),
    );
    return dataGroupedWithSumsFiltered;
}

const getDateAxisLabelColor =
    (theme: Theme, isMonthly?: boolean, exercice?: number) =>
    (value: string): string => {
        if (!isMonthly || !exercice) {
            return theme.palette.darkGrey;
        }
        const isLastYearColumn = value.startsWith(String(exercice - 1));
        return isLastYearColumn
            ? theme.palette.grey[400]
            : theme.palette.darkGrey;
    };

// Big hack here. To display the total amount of each bar (date)
// we add a serie of zeros (not to fake the graph) invisible
// and we compute the total in the label formatter callback to display it
const getTotalLabelsSerie = (
    vizData: Record<string | DateString, string | number>[],
    dates: DateString[],
): SeriesOption => ({
    type: 'bar',
    stack: 'total',
    data: dates.map(() => 0),
    itemStyle: {
        color: 'transparent', // make this bar invisible
    },
    label: {
        show: true,
        position: 'top',
        formatter: (params: DefaultLabelFormatterCallbackParams) => {
            const date = dates[params.dataIndex];
            const totalDate = _.sumBy(vizData, date);
            return currencyFormatter(totalDate);
        },
    },
    tooltip: {
        show: false,
    },
});

type OptionsArgs = {
    vizData: Record<string | DateString, string | number>[];
    groupField: string;
    valueField: string;
    filteredDates: DateString[];
    isMonthly: boolean;
    exercice: number;
    theme: Theme;
    t: TranslationT;
};

export const getDatedBarGraphOptions = ({
    vizData,
    groupField,
    valueField,
    filteredDates,
    isMonthly,
    exercice,
    theme,
    t,
}: OptionsArgs): EChartsOption => {
    const series: SeriesOption[] = vizData.map((datum) => ({
        name: getGroupFieldValue(datum, groupField),
        type: 'bar',
        stack: 'total',
        emphasis: {
            focus: 'series',
        },
        data: filteredDates.map((date) => datum[date]),
    }));

    const totalSerie = getTotalLabelsSerie(vizData, filteredDates);
    return {
        xAxis: {
            type: 'category',
            data: filteredDates.map((date) =>
                getDateColumnLabel(date, isMonthly),
            ),
            axisLabel: {
                color: getDateAxisLabelColor(theme, isMonthly, exercice),
            },
        },

        yAxis: {
            type: 'value',
            name: t(`humanResources.payState.${valueField}`),
            nameTextStyle: { fontSize: 14, align: 'right' },
            axisLabel: {
                formatter: currencyFormatter,
            },
        },
        series: [...series, totalSerie],
        tooltip: {
            ...DEFAULT_OPTIONS.tooltip,
            valueFormatter: currencyFormatter,
        },
        legend: {
            ...DEFAULT_OPTIONS.legend,
            selectedMode: false,
        },
    };
};
